import { useEffect, useState } from "react";
import iconNavyGold from "@/assets/enviroeng_icon_navy_gold.png.asset.json";
import iconDarkTeal from "@/assets/enviroeng_icon_dark_teal.png.asset.json";

const LOADING_MESSAGES = [
  "Warming up sensors…",
  "Syncing environmental data…",
  "Preparing your workspace…",
];

// Splash renders before React theme hydration — use CSS prefers-color-scheme
// so the right icon shows on first paint.
export default function SplashScreen() {
  const [msgIndex, setMsgIndex] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => {
      setMsgIndex((i) => (i + 1) % LOADING_MESSAGES.length);
    }, 1600);
    return () => window.clearInterval(id);
  }, []);

  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label="Loading EnviroEng Pro"
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-background"
      style={{ backgroundImage: "var(--gradient-hero)" }}
    >
      {/* Theme-tinted glow: navy in light, teal in dark */}
      <div className="absolute w-72 h-72 rounded-full blur-3xl animate-pulse bg-[hsl(var(--brand-navy)/0.14)] dark:bg-[hsl(var(--brand-teal)/0.22)]" />
      <div className="absolute w-40 h-40 rounded-full blur-2xl bg-[hsl(var(--brand-gold)/0.10)]" />

      <div className="relative animate-[fadeInScale_0.6s_ease-out_both]">
        <img
          src={iconNavyGold.url}
          alt="EnviroEng Pro"
          width={128}
          height={128}
          fetchPriority="high"
          decoding="async"
          loading="eager"
          className="h-[96px] w-auto drop-shadow-lg dark:hidden"
        />
        <img
          src={iconDarkTeal.url}
          alt="EnviroEng Pro"
          width={128}
          height={128}
          fetchPriority="high"
          decoding="async"
          loading="eager"
          className="hidden h-[96px] w-auto drop-shadow-lg dark:block"
        />
      </div>

      <h1 className="mt-6 text-xl font-bold tracking-tight text-foreground animate-[fadeInUp_0.6s_ease-out_0.2s_both] font-[Sora]">
        EnviroEng Pro
      </h1>
      <div className="mt-2 h-px w-24 brand-hairline animate-[fadeInUp_0.6s_ease-out_0.3s_both]" />
      <p className="mt-2 text-xs text-muted-foreground animate-[fadeInUp_0.6s_ease-out_0.35s_both]">
        Environmental Intelligence Platform
      </p>

      {/* Indeterminate shimmer progress bar */}
      <div
        className="mt-6 h-1 w-40 overflow-hidden rounded-full bg-muted/50 animate-[fadeInUp_0.6s_ease-out_0.45s_both]"
        aria-hidden="true"
      >
        <div
          className="h-full w-1/2 rounded-full"
          style={{
            background:
              "linear-gradient(90deg, transparent, hsl(var(--brand-gold)) 40%, hsl(var(--brand-teal)) 60%, transparent)",
            animation: "shimmer 1.4s linear infinite",
          }}
        />
      </div>

      {/* Rotating loading microcopy */}
      <p
        key={msgIndex}
        className="mt-3 text-xs text-muted-foreground animate-[fadeInUp_0.5s_ease-out_both] min-h-[1rem]"
      >
        {LOADING_MESSAGES[msgIndex]}
      </p>

      <div className="mt-4 flex gap-1.5 animate-[fadeInUp_0.6s_ease-out_0.5s_both]" aria-hidden="true">
        <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--brand-teal))] animate-[bounce_1s_infinite_0ms]" />
        <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--brand-gold))] animate-[bounce_1s_infinite_150ms]" />
        <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--brand-teal))] animate-[bounce_1s_infinite_300ms]" />
      </div>

      <span className="sr-only">Loading application…</span>
    </div>
  );

}
